import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CantoiaLogo from '../../../../assets/img/logos/cantoia.png';
import MESSAGES from "../../../../common/data/constants/messages";
import { IAnsweredSurveyEmployee, IEvidenceImageAnswered } from "../../../../common/data/dtos/answered-survey-employee";
import SurveyQuestionItemTypeEnum from "../../../../common/data/enums/enumSurveyQuestionItemType";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import Textarea from "../../../../components/bootstrap/forms/Textarea";
import { DownloadAnsweredSurveyReport } from "../../../../components/extras/DownloadAnsweredSurveyReport";
import { showWarning } from "../../../../components/extras/Notifications";
import Icon from "../../../../components/icon/Icon";
import answeredSurveyService from "../../../../services/answered-survey-service";
import fileService from "../../../../services/file-service";
import LoadingPage from "../../loading/LoadingPage";
import { AnsweredSurveyDetailModal } from "./AnsweredSurveyDetailModal";

export const CommonAnsweredSurveyDetail = () => {

    const { id } = useParams();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [isModalOpened, setIsModalOpened] = useState(false);
    const [selectedEvidenceImage, setSelectedEvidenceImage] = useState<string>('');

    const [answeredSurvey, setAnsweredSurvey] = useState<IAnsweredSurveyEmployee | undefined>(undefined);
    const [teamLeaderSignUrl, setTeamLeaderSignUrl] = useState<string>('');
    const [inspectionResponsibleSignUrl, setInspectionResponsibleSignUrl] = useState<string>('');

    useEffect(() => {
        if (id) {
            fetchAnsweredSurvey();
        }
    }, []);

    const getKey = (url: string) => {
        const parsedUrl = new URL(url);
        const path = parsedUrl.pathname;
        const key = path.substring(path.lastIndexOf('/') + 1);

        return key;
    }

    const fetchAnsweredSurvey = async () => {
        try {
            if (!id) {
                showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
                return;
            }

            setIsLoading(true);

            const response = await answeredSurveyService.getOneAnsweredSurvey(id);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            setAnsweredSurvey(response.answeredSurveyEmployee);

            const answeredSurvey = response.answeredSurveyEmployee;

            const promises: Promise<any>[] = [];
            const promiseSources: ('teamLeader' | 'inspectionResponsible')[] = [];

            if (answeredSurvey?.hasTeamLeader && answeredSurvey.teamLeaderSign && answeredSurvey.teamLeaderSign?.length > 0) {
                const key = getKey(answeredSurvey.teamLeaderSign);
                promises.push(fileService.getSignedUrl(key));
                promiseSources.push('teamLeader');
            }

            if (answeredSurvey?.hasInspectionResponsible && answeredSurvey?.inspectionResponsibleSign && answeredSurvey?.inspectionResponsibleSign?.length > 0) {
                const key = getKey(answeredSurvey.inspectionResponsibleSign);
                promises.push(fileService.getSignedUrl(key));
                promiseSources.push('inspectionResponsible');
            }

            if (promises.length === 0) {
                return;
            }

            const results = await Promise.all(promises);

            for (let i = 0; i < results.length; i++) {
                const result = results[i];
                const source = promiseSources[i];

                if (result.error) {
                    showWarning(result.error);
                    return;
                }

                if (source === 'teamLeader') {
                    setTeamLeaderSignUrl(result.url || '');
                } else if (source === 'inspectionResponsible') {
                    setInspectionResponsibleSignUrl(result.url || '');
                }
            }

        } catch {
            showWarning('Erro ao carregar questionário');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleModal = (isVisible: boolean) => {
        setIsModalOpened(isVisible);
    };

    const onModalClose = () => {
        setSelectedEvidenceImage('');
        toggleModal(false);
    };

    const onAfterSubmit = async () => {
        onModalClose();
    };

    const handleViewClick = async (item: IEvidenceImageAnswered) => {

        try {
            if (!item.evidenceImage) {
                showWarning('Imagem não encontrada');
                return;
            }

            const key = getKey(item.evidenceImage);
            if (!key) {
                showWarning('Chave da imagem não encontrada');
                return;
            }

            setIsLoading(true);

            const response = await fileService.getEvidenceImageUrl(key);
            if (response.error) {
                showWarning(response.error);
                return;
            }

            setSelectedEvidenceImage(response.url || '');
            toggleModal(true);
        } catch {
            showWarning('Falha ao recuperar imagem')
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <LoadingPage />;
    }

    return (
        <>
            {
                answeredSurvey && (

                    <>
                        {/* Nome do questionário */}
                        <div className='row g-4 align-items-center'>
                            <div className='col-auto'>
                                <img src={CantoiaLogo} alt='Cantoia' width={150} />
                            </div>
                            <div className='col'>
                                <div className='display-6 fw-bold py-3'>{answeredSurvey.name}</div>
                            </div>
                            <div className='col-auto'>
                                {
                                    id && (
                                        <DownloadAnsweredSurveyReport surveyId={id} />
                                    )
                                }
                            </div>
                        </div>

                        {/* Cabeçalhos */}
                        {
                            answeredSurvey.headers && (
                                <>
                                    {answeredSurvey.headers.map((header, index) => (
                                        <div key={index} className='row g-4'>
                                            <Card style={{ minHeight: 100 }}>
                                                {header.title && (
                                                    <CardHeader>
                                                        <CardLabel icon='ViewHeadline' iconColor='primary'>
                                                            <CardTitle tag='div' className='h5'>
                                                                {header.title}
                                                            </CardTitle>
                                                        </CardLabel>
                                                    </CardHeader>
                                                )}

                                                <CardBody>
                                                    {header.surveyHeaderItems && header.surveyHeaderItems.length > 0 && (
                                                        <div className='row g-4'>
                                                            {header.surveyHeaderItems.map((headerItem, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    className='col-xl-4 col-lg-6 col-md-12 col-sm-12'
                                                                >
                                                                    <FormGroup id={`field_${idx}`} label={headerItem.item} isFloating>
                                                                        <Input value={headerItem.answer} />
                                                                    </FormGroup>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        </div>
                                    ))}
                                </>
                            )
                        }

                        {/* Questões */}
                        {answeredSurvey.questions && (
                            <div className='row g-4'>
                                <Card style={{ minHeight: 100 }}>
                                    <CardHeader>
                                        <CardLabel icon='ViewList' iconColor='primary'>
                                            <CardTitle tag='div' className='h5'>Questões</CardTitle>
                                        </CardLabel>
                                    </CardHeader>

                                    <CardBody>
                                        {/* Questões ALTERNATIVAS */}
                                        {
                                            answeredSurvey.questions.map((question, qIndex) => {
                                                const alternatives = question.items?.filter(item => item.type === SurveyQuestionItemTypeEnum.alternative) || [];
                                                if (alternatives.length === 0)
                                                    return null;

                                                return (
                                                    <div className='row g-4 table-responsive' key={`alt-${qIndex}`}>
                                                        <table className='table table-modern table-hover'>
                                                            <thead>
                                                                <tr>
                                                                    <th>{question.title}</th>
                                                                    <th className="text-center">Sim</th>
                                                                    <th className="text-center">Não</th>
                                                                    <th className="text-center">NA</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {alternatives.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <td>{item.question}</td>
                                                                        <td style={{ width: '5%' }} className="text-center">{item.answer === 'S' ? <Icon icon='Close' color='dark' size='lg' /> : ''}</td>
                                                                        <td style={{ width: '5%' }} className="text-center">{item.answer === 'N' ? <Icon icon='Close' color='dark' size='lg' /> : ''}</td>
                                                                        <td style={{ width: '5%' }} className="text-center">{item.answer === 'NA' ? <Icon icon='Close' color='dark' size='lg' /> : ''}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                );
                                            })}

                                        {/* Questões DESCRITIVAS */}
                                        {
                                            answeredSurvey.questions.map((question, qIndex) => {
                                                const descriptives = question.items?.filter(item => item.type === SurveyQuestionItemTypeEnum.descriptive) || [];
                                                if (descriptives.length === 0)
                                                    return null;

                                                return (
                                                    <div className='row g-4 table-responsive' key={`alt-${qIndex}`}>
                                                        <table className='table table-modern table-hover'>
                                                            <tbody>
                                                                {descriptives.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <td>{item.question}</td>
                                                                        <td style={{ width: '15%' }}>{item.answer}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                );
                                            })}
                                    </CardBody>
                                </Card>
                            </div>
                        )}

                        {/* Observações */}
                        {
                            answeredSurvey.hasObservation && answeredSurvey.observation && answeredSurvey.observation.length > 0 && (
                                <>
                                    <div className='row g-4'>
                                        <Card style={{ minHeight: 100 }}>
                                            <CardHeader>
                                                <CardLabel icon='Comment' iconColor='primary'>
                                                    <CardTitle tag='div' className='h5'>
                                                        Observações
                                                    </CardTitle>
                                                </CardLabel>
                                            </CardHeader>

                                            <CardBody>
                                                <div className='row g-4'>
                                                    <div className='col-xl-12 col-lg-12 col-md-12 col-sm-12'>
                                                        <FormGroup label='Observações' isFloating>
                                                            <Textarea value={answeredSurvey.observation} className={'h-100'} />
                                                        </FormGroup>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </div>
                                </>
                            )
                        }

                        {/* Lider Tecnico e Responsável pela equipe */}
                        {
                            (answeredSurvey.hasTeamLeader || answeredSurvey.hasInspectionResponsible) && (

                                <div className='row g-4'>
                                    <Card style={{ minHeight: 100 }}>
                                        <CardHeader>
                                            <CardLabel icon='Engineering' iconColor='primary'>
                                                <CardTitle tag='div' className='h5'>
                                                    Responsáveis
                                                </CardTitle>
                                            </CardLabel>
                                        </CardHeader>

                                        <CardBody>
                                            <div className='row g-4'>

                                                {
                                                    answeredSurvey.hasTeamLeader && (
                                                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12'>
                                                            <FormGroup label='Responsável pela equipe' isFloating>
                                                                <Input value={answeredSurvey.teamLeaderName} />
                                                            </FormGroup>
                                                        </div>
                                                    )
                                                }

                                                {
                                                    answeredSurvey.hasInspectionResponsible && (
                                                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12'>
                                                            <FormGroup label='Responsável pela inspeção' isFloating>
                                                                <Input value={answeredSurvey.inspectionResponsibleName} />
                                                            </FormGroup>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            )
                        }

                        {/* Responsável pela equipe */}
                        {
                            answeredSurvey.hasTeamLeader && answeredSurvey.teamLeaderSign && answeredSurvey.teamLeaderSign.length > 0 && (
                                <>
                                    <div className='row g-4'>
                                        <Card style={{ minHeight: 100 }}>
                                            <CardHeader>
                                                <CardLabel icon='Engineering' iconColor='primary'>
                                                    <CardTitle tag='div' className='h5'>
                                                        Responsável pela equipe
                                                    </CardTitle>
                                                </CardLabel>
                                            </CardHeader>

                                            <CardBody>
                                                <div className='row g-4'>
                                                    <div className='col-lg-12 col-md-12 col-sm-12 d-flex justify-content-center'>
                                                        <div
                                                            style={{
                                                                border: '2px dashed #aaa',
                                                                borderRadius: '8px',
                                                                padding: '8px',
                                                                backgroundColor: '#fdfdfd',
                                                                textAlign: 'center',
                                                            }}
                                                        >
                                                            <img
                                                                src={teamLeaderSignUrl}
                                                                alt="Assinatura"
                                                                style={{
                                                                    width: 500,
                                                                    height: 400,
                                                                    transform: 'rotate(-90deg)', // gira 90 graus no sentido horário
                                                                    transformOrigin: 'center center',
                                                                }}

                                                            />

                                                            <hr style={{ width: '60%', margin: '8px auto', borderTop: '1px solid #333' }} />

                                                            <p className='lead'>
                                                                {'Responsável pela equipe'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </div>
                                </>
                            )
                        }

                        {/* Responsável pela Inspeção */}
                        {
                            answeredSurvey.hasInspectionResponsible && answeredSurvey.inspectionResponsibleSign && answeredSurvey.inspectionResponsibleSign.length > 0 && (
                                <>
                                    <div className='row g-4'>
                                        <Card style={{ minHeight: 100 }}>
                                            <CardHeader>
                                                <CardLabel icon='PersonSearch' iconColor='primary'>
                                                    <CardTitle tag='div' className='h5'>
                                                        Responsável pela inspeção
                                                    </CardTitle>
                                                </CardLabel>
                                            </CardHeader>

                                            <CardBody>
                                                <div className='row g-4'>
                                                    <div className='col-lg-12 col-md-12 col-sm-12 d-flex justify-content-center'>
                                                        <div
                                                            style={{
                                                                border: '2px dashed #aaa',
                                                                borderRadius: '8px',
                                                                padding: '8px',
                                                                backgroundColor: '#fdfdfd',
                                                                textAlign: 'center',
                                                            }}
                                                        >
                                                            <img
                                                                src={inspectionResponsibleSignUrl}
                                                                alt="Assinatura"
                                                                style={{
                                                                    width: 500,
                                                                    height: 400,
                                                                    transform: 'rotate(-90deg)', // gira 90 graus no sentido horário
                                                                    transformOrigin: 'center center',
                                                                }}
                                                            />

                                                            <hr style={{ width: '60%', margin: '8px auto', borderTop: '1px solid #333' }} />

                                                            <p className='lead'>
                                                                {'Responsável pela inspeção'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </div>
                                </>
                            )
                        }

                        {/* Evidências */}
                        {
                            answeredSurvey.evidenceImages && (
                                <>
                                    <div className='row g-4'>
                                        <Card style={{ minHeight: 100 }}>
                                            <CardHeader>
                                                <CardLabel icon='Image' iconColor='primary'>
                                                    <CardTitle tag='div' className='h5'>
                                                        Evidências
                                                    </CardTitle>
                                                </CardLabel>
                                            </CardHeader>

                                            <CardBody className='table-responsive'>
                                                <table className='table table-modern table-hover'>
                                                    <thead>
                                                        <tr>
                                                            <th>Descrição</th>
                                                            <td aria-labelledby='Actions' />
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {answeredSurvey.evidenceImages.map((item: IEvidenceImageAnswered, index) => (
                                                            <tr key={index}>
                                                                <td>{item.description}</td>

                                                                {
                                                                    item.evidenceImage && (

                                                                        <td className='text-end'>
                                                                            <Button
                                                                                color='dark'
                                                                                isLight
                                                                                icon='RemoveRedEye'
                                                                                onClick={() => handleViewClick(item)}
                                                                                aria-label='Visualizar'
                                                                            />
                                                                        </td>
                                                                    )
                                                                }
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </CardBody>
                                        </Card>

                                        <AnsweredSurveyDetailModal
                                            isModalOpened={isModalOpened}
                                            selectedEvidenceImage={selectedEvidenceImage}
                                            onModalClose={onModalClose}
                                            onAfterSubmit={onAfterSubmit}
                                        />
                                    </div>
                                </>
                            )
                        }
                    </>
                )
            }
        </>
    );
}
